// Sprint S20.5 PR-3 (D013-EC-69) — persistent runner-status banner.
//
// Founder symptom: when one or more runners report `unavailable_reason`
// (account blocked / disk full / not authenticated / incompatible
// version) or `status: 'offline'`, the only signal today is the toast
// the next failed spawn surfaces. The founder has no way to look at the
// app and SEE that the deployment is running degraded — they have to
// either trigger a failure or open Settings → Servers manually.
//
// Cursor handles the same class of problem with a sticky topbar strip
// when the local sidecar is degraded (`cursor-ux-mirror.md` §1). We
// mirror the pattern: a thin warning strip below the network banner,
// always visible while at least one runner is unhealthy, that links
// straight to `/settings#runners`.
//
// Three visual states (all warning-coloured, severity escalates):
//
//   1. some unavailable, some healthy → "{count} server(s) offline.
//      New agents will route to the rest." (informational)
//   2. every runner unavailable        → "All servers offline. New
//      agents can't be created right now." (blocking)
//   3. zero runners loaded             → render NOTHING (we never
//      block the chrome on a not-yet-hydrated store).
//
// Mounting: `DashboardLayout` renders this directly under
// `<NetworkBanner />` so the stack is `network → runner-status →
// chrome`. Both banners use the same `bg-status-warning/15 …
// border-status-warning/30` token combo so they read as one
// "system-degraded" surface when both apply.

import { useNavigate } from 'react-router-dom';
import { Icon } from '../atoms/Icon';
import { useRunnersStore } from '../../stores/runners.store';
import { useTranslation } from '../../i18n';
import type { RunnerSnapshot } from '../../lib/api';

// A runner is "unhealthy" when it can't accept a new agent right now.
// Mirrors the scheduler's view in `runner-router.ts`:
//   - explicit `unavailable_reason` set (auth, disk, blocked, version)
//   - status is `offline` or `incompatible_version`
//   - drain mode (operator is taking it out of rotation cleanly)
function isUnhealthy(r: RunnerSnapshot): boolean {
  if (r.unavailableReason !== null) return true;
  if (r.status === 'offline') return true;
  if (r.status === 'incompatible_version') return true;
  if (r.drainMode) return true;
  return false;
}

export function RunnerStatusBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const runnersById = useRunnersStore((s) => s.runnersById);
  const loaded = useRunnersStore((s) => s.loaded);

  // Pre-hydrate: never render. The chrome gets the banner only after
  // the runners store has at least one snapshot, otherwise the page
  // would briefly flash an "All servers offline" panic on first load.
  if (!loaded) return null;

  const runners = Object.values(runnersById);
  if (runners.length === 0) return null;

  const unhealthyCount = runners.reduce(
    (n, r) => (isUnhealthy(r) ? n + 1 : n),
    0,
  );
  if (unhealthyCount === 0) return null;

  const allDown = unhealthyCount === runners.length;

  let message: string;
  if (allDown) {
    message = t('topbar.runnerBanner.allDown');
  } else if (unhealthyCount === 1) {
    message = t('topbar.runnerBanner.unavailableOne');
  } else {
    message = t('topbar.runnerBanner.unavailableMany', { count: unhealthyCount });
  }

  function goToServers() {
    // SettingsPage doesn't yet anchor-scroll on hash; the runners
    // panel is one screen down. Navigate to `/settings` and let the
    // founder land on top of the page — they see the structure and
    // scroll to Runners. Adding hash-anchor scrolling is its own UX
    // sprint (would also need to coexist with the focus-on-mount
    // patterns the SettingsPage already owns).
    navigate('/settings');
  }

  // Reviewer note (PR #148): a single `<button role="alert">` collides
  // semantically — `role="alert"` overrides the implicit `button` role
  // at the AT layer, so the click affordance becomes invisible to
  // screen readers. Split into a live-region container + a real
  // `<button>` action inside, mirroring how Cursor's degraded-state
  // strip exposes the affordance.
  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid="runner-status-banner"
      data-unhealthy-count={unhealthyCount}
      data-all-down={allDown ? 'true' : 'false'}
      className={[
        'w-full flex items-center justify-center gap-2 px-4 py-1.5',
        'pt-safe-top box-content text-body text-foreground',
        'bg-status-warning/15 border-b border-status-warning/30',
        'animate-fade-in',
      ].join(' ')}
    >
      <Icon
        name="AlertCircle"
        size={14}
        className="text-status-warning shrink-0"
        aria-hidden
      />
      <span className="text-status-warning">{message}</span>
      <button
        type="button"
        onClick={goToServers}
        data-testid="runner-status-banner-action"
        className={[
          'text-caption text-muted-foreground hover:text-foreground',
          'transition-colors duration-motion-default ease-cursor-out',
          'focus:outline-none focus:ring-2 focus:ring-status-warning/40',
          'rounded px-1',
        ].join(' ')}
      >
        {t('topbar.runnerBanner.goToServers')}
      </button>
    </div>
  );
}
